// CANONICAL COPY (GH #7115). src/constants/scfControls.js re-exports this file so the browser and the
// Function App share one compliance engine — one definition of "compliant". Edit here, never there.
'use strict';

/**
 * Tryggr Control Framework (TCF)
 *
 * Canonical, vendor-neutral compliance control library.
 * All external frameworks (ISO 27001, CIS18, NIST CSF, etc.) map into these controls.
 */

const SCF_DOMAINS = [
  {
    code: 'MSS',
    name: 'Management System',
    color: '#94a3b8',
    description: 'High-level structure clauses shared by all ISO management system standards',
    future: false,
  },
  {
    code: 'GOV',
    name: 'Governance & Organization',
    color: '#5eead4',
    description: 'Policies, roles, legal compliance and governance',
    future: false,
  },
  {
    code: 'RSK',
    name: 'Risk Management',
    color: '#fb923c',
    description: 'Risk assessment, treatment and security objectives',
    future: false,
  },
  {
    code: 'AUD',
    name: 'Compliance & Audit',
    color: '#fbbf24',
    description: 'Independent review, audit programmes and compliance checks',
    future: false,
  },
  {
    code: 'IAM',
    name: 'Identity & Access',
    color: '#a78bfa',
    description: 'Identity management, authentication and access control',
    future: false,
  },
  {
    code: 'AST',
    name: 'Asset Management',
    color: '#38bdf8',
    description: 'Asset inventory, classification, labelling and transfer',
    future: false,
  },
  {
    code: 'PPL',
    name: 'People & Employment',
    color: '#f472b6',
    description: 'Screening, training, HR security and remote working',
    future: false,
  },
  {
    code: 'PHY',
    name: 'Physical Security',
    color: '#86efac',
    description: 'Physical perimeters, entry, equipment and environmental controls',
    future: false,
  },
  {
    code: 'OPS',
    name: 'Operations & Technology',
    color: '#67e8f9',
    description: 'Endpoints, malware, logging, config and operational controls',
    future: false,
  },
  {
    code: 'NET',
    name: 'Network Security',
    color: '#60a5fa',
    description: 'Network management, segregation, filtering and cryptography',
    future: false,
  },
  {
    code: 'DEV',
    name: 'Secure Development',
    color: '#c084fc',
    description: 'SDLC, secure coding, testing and change management',
    future: false,
  },
  {
    code: 'INC',
    name: 'Incident Management',
    color: '#fb7185',
    description: 'Incident response, classification, learning and evidence',
    future: false,
  },
  {
    code: 'BCM',
    name: 'Business Continuity',
    color: '#34d399',
    description: 'Continuity planning, ICT resilience and backups',
    future: false,
  },
  {
    code: 'VDR',
    name: 'Vendor & Supply Chain',
    color: '#fcd34d',
    description: 'Supplier relationships, agreements and cloud services',
    future: false,
  },
  {
    code: 'DAT',
    name: 'Data Protection',
    color: '#e879f9',
    description: 'Records, privacy, deletion, masking and data leakage',
    future: false,
  },
  {
    code: 'QMS',
    name: 'Quality Management',
    color: '#d1fae5',
    description: 'ISO 9001 quality management (future)',
    future: true,
  },
  {
    code: 'ENV',
    name: 'Environmental Management',
    color: '#bbf7d0',
    description: 'ISO 14001 environmental management (future)',
    future: true,
  },
  {
    code: 'AIG',
    name: 'AI Governance',
    color: '#ddd6fe',
    description:
      'AI management, impact assessment, data quality, lifecycle, human oversight and third-party AI — supporting ISO 42001 and the EU AI Act',
    future: false,
  },
  {
    code: 'PRV',
    name: 'Privacy Rights',
    color: '#e879f9',
    description: 'GDPR data subject rights, privacy notices and consent management',
    future: false,
  },
];

const SCF_CONTROLS = [
  // ── MSS — Management System ──────────────────────────────────────────
  {
    id: 'SCF-MSS-001',
    domain: 'MSS',
    title: 'Context and scope',
    description:
      'The organization shall determine external and internal issues relevant to its purpose and establish the scope and boundaries of the management system.',
  },
  {
    id: 'SCF-MSS-002',
    domain: 'MSS',
    title: 'Resources',
    description:
      'The organization shall determine and provide the resources needed for the establishment, implementation, maintenance and continual improvement of the management system.',
  },
  {
    id: 'SCF-MSS-003',
    domain: 'MSS',
    title: 'Communication and awareness',
    description:
      'The organization shall determine the need for internal and external communications relevant to the management system and ensure relevant persons are aware of the system and their contribution to it.',
  },
  {
    id: 'SCF-MSS-004',
    domain: 'MSS',
    title: 'Operational planning and control',
    description:
      'The organization shall plan, implement, control, monitor and review the processes needed to meet requirements and implement treatment actions, including management of planned and unintended changes.',
  },
  {
    id: 'SCF-MSS-005',
    domain: 'MSS',
    title: 'Performance monitoring and measurement',
    description:
      'The organization shall determine what needs to be monitored and measured, including methods, frequency and who performs evaluations, and shall retain documented evidence of results.',
  },
  {
    id: 'SCF-MSS-006',
    domain: 'MSS',
    title: 'Management review',
    description:
      'Top management shall review the management system at planned intervals to ensure its continuing suitability, adequacy and effectiveness, considering inputs on performance, changes and improvement opportunities.',
  },
  {
    id: 'SCF-MSS-007',
    domain: 'MSS',
    title: 'Continual improvement and corrective action',
    description:
      'The organization shall continually improve the suitability and effectiveness of the management system and react to nonconformities with corrective actions, retaining documented evidence.',
  },

  // ── GOV — Governance & Organization ───────────────────────────────────
  {
    id: 'SCF-GOV-001',
    domain: 'GOV',
    title: 'Information security policies',
    description:
      'Information security policies shall be defined, approved by management, published, communicated to relevant personnel and reviewed at planned intervals or when significant changes occur.',
  },
  {
    id: 'SCF-GOV-002',
    domain: 'GOV',
    title: 'Information security roles and responsibilities',
    description:
      "Information security roles and responsibilities shall be defined, allocated and communicated according to the organization's needs.",
  },
  {
    id: 'SCF-GOV-003',
    domain: 'GOV',
    title: 'Segregation of duties',
    description:
      'Conflicting duties and conflicting areas of responsibility shall be segregated to reduce the risk of unauthorized or unintentional modification or misuse of assets.',
  },
  {
    id: 'SCF-GOV-004',
    domain: 'GOV',
    title: 'Management responsibilities',
    description:
      'Management shall demonstrate leadership and require all personnel to apply information security in accordance with established policies and procedures of the organization.',
  },
  {
    id: 'SCF-GOV-005',
    domain: 'GOV',
    title: 'Contact with authorities',
    description:
      'The organization shall establish and maintain contact with relevant external authorities including law enforcement, regulators and emergency services.',
  },
  {
    id: 'SCF-GOV-006',
    domain: 'GOV',
    title: 'Contact with special interest groups',
    description:
      'The organization shall establish and maintain contact with special interest groups, security forums and professional associations to stay current with threats and best practices.',
  },
  {
    id: 'SCF-GOV-007',
    domain: 'GOV',
    title: 'Threat intelligence',
    description:
      "Information relating to information security threats shall be collected, analysed and used to produce actionable threat intelligence relevant to the organization's risk profile.",
  },
  {
    id: 'SCF-GOV-008',
    domain: 'GOV',
    title: 'Security in project management',
    description:
      "Information security shall be integrated into the organization's project management methodology throughout the project lifecycle.",
  },
  {
    id: 'SCF-GOV-009',
    domain: 'GOV',
    title: 'Legal, regulatory and contractual requirements',
    description:
      'Legal, statutory, regulatory and contractual requirements relevant to information security shall be identified, documented and kept up to date.',
  },
  {
    id: 'SCF-GOV-010',
    domain: 'GOV',
    title: 'Intellectual property rights',
    description:
      'The organization shall implement appropriate procedures to protect intellectual property rights, including for software, documentation and data.',
  },
  {
    id: 'SCF-GOV-011',
    domain: 'GOV',
    title: 'Documented operating procedures',
    description:
      'Operating procedures for information processing facilities and security-relevant operations shall be documented, maintained and made available to personnel who need them.',
  },
  {
    id: 'SCF-GOV-012',
    domain: 'GOV',
    title: 'Management body accountability',
    description:
      'The management body of the entity shall approve and oversee cybersecurity risk-management measures. Management body members shall be responsible for compliance with cybersecurity obligations and may be held personally liable for infringements.',
  },

  // ── RSK — Risk Management ─────────────────────────────────────────────
  {
    id: 'SCF-RSK-001',
    domain: 'RSK',
    title: 'Risk assessment process',
    description:
      'A risk assessment process shall be defined and applied to identify, analyse and evaluate information security risks, producing consistent and comparable results at planned intervals and when significant changes occur.',
  },
  {
    id: 'SCF-RSK-002',
    domain: 'RSK',
    title: 'Information security objectives',
    description:
      'Information security objectives shall be established at relevant functions and levels, be consistent with the security policy, be measurable where practicable, and be monitored and updated as appropriate.',
  },

  // ── AUD — Compliance & Audit ──────────────────────────────────────────
  {
    id: 'SCF-AUD-001',
    domain: 'AUD',
    title: 'Independent review of security',
    description:
      "The organization's approach to managing information security, including people, processes and technologies, shall be reviewed independently at planned intervals or when significant changes occur.",
  },
  {
    id: 'SCF-AUD-002',
    domain: 'AUD',
    title: 'Compliance with policies and standards',
    description:
      "Compliance with the organization's information security policies, topic-specific policies, rules and standards shall be regularly reviewed and deviations addressed.",
  },
  {
    id: 'SCF-AUD-003',
    domain: 'AUD',
    title: 'Penetration testing programme',
    description:
      'Penetration testing shall be conducted by a qualified internal resource or qualified external third party at least annually and after any significant infrastructure or application changes. Tests shall include network and application layer testing. Results shall be documented, risk-ranked, and remediated within defined timelines.',
  },
  {
    id: 'SCF-AUD-004',
    domain: 'AUD',
    title: 'Vulnerability scanning programme',
    description:
      'Internal vulnerability scans shall be performed at least quarterly and after any significant network change. External vulnerability scans shall be performed at least quarterly by a qualified scanning vendor. All high-risk and critical vulnerabilities shall be addressed and rescanned to confirm resolution.',
  },

  // ── IAM — Identity & Access ───────────────────────────────────────────
  {
    id: 'SCF-IAM-001',
    domain: 'IAM',
    title: 'Access control policy',
    description:
      'Rules to control physical and logical access to information and other assets shall be established and implemented based on business and information security requirements.',
  },
  {
    id: 'SCF-IAM-002',
    domain: 'IAM',
    title: 'Identity management',
    description:
      'The full lifecycle of identities shall be managed, including establishment, maintenance, and removal, to ensure only authorised identities exist and are appropriately provisioned.',
  },
  {
    id: 'SCF-IAM-003',
    domain: 'IAM',
    title: 'Authentication information management',
    description:
      'Allocation and management of authentication information, including passwords and credentials, shall be controlled through a formal management process.',
  },
  {
    id: 'SCF-IAM-004',
    domain: 'IAM',
    title: 'Access rights lifecycle',
    description:
      'Access rights to information and assets shall be provisioned, reviewed, modified and revoked in accordance with the access control policy and business requirements.',
  },
  {
    id: 'SCF-IAM-005',
    domain: 'IAM',
    title: 'Privileged access rights',
    description:
      'The allocation and use of privileged access rights shall be restricted, managed and monitored, with privileges granted only on a need-to-use basis.',
  },
  {
    id: 'SCF-IAM-006',
    domain: 'IAM',
    title: 'Information access restriction',
    description:
      'Access to information and system functions shall be restricted in accordance with the access control policy, implementing least-privilege principles.',
  },
  {
    id: 'SCF-IAM-007',
    domain: 'IAM',
    title: 'Access to source code',
    description:
      'Access to source code, development tools and software libraries shall be appropriately restricted to protect against the introduction of unauthorized functionality and the removal of controls.',
  },
  {
    id: 'SCF-IAM-008',
    domain: 'IAM',
    title: 'Secure authentication',
    description:
      'Secure authentication technologies and procedures shall be implemented to verify the identity of users, systems and services accessing information assets.',
  },

  // ── AST — Asset Management ────────────────────────────────────────────
  {
    id: 'SCF-AST-001',
    domain: 'AST',
    title: 'Asset inventory',
    description:
      'An inventory of information and associated assets shall be developed, documented, maintained and kept accurate, including identification of ownership and criticality.',
  },
  {
    id: 'SCF-AST-002',
    domain: 'AST',
    title: 'Acceptable use of assets',
    description:
      'Rules for the acceptable use and handling of information and associated assets shall be identified, documented, implemented and communicated to all relevant personnel.',
  },
  {
    id: 'SCF-AST-003',
    domain: 'AST',
    title: 'Return of assets',
    description:
      'Personnel and relevant parties shall return all organizational assets in their possession upon termination or change of employment, contract or agreement.',
  },
  {
    id: 'SCF-AST-004',
    domain: 'AST',
    title: 'Information classification',
    description:
      'Information shall be classified according to legal requirements and its value, sensitivity and criticality to the organization, considering confidentiality, integrity and availability.',
  },
  {
    id: 'SCF-AST-005',
    domain: 'AST',
    title: 'Information labelling',
    description:
      "An appropriate set of procedures for information labelling shall be developed and implemented in accordance with the organization's information classification scheme.",
  },
  {
    id: 'SCF-AST-006',
    domain: 'AST',
    title: 'Information transfer',
    description:
      'Rules, procedures or agreements shall govern information transfer for all types of transfer facilities within the organization and between the organization and external parties.',
  },

  // ── PPL — People & Employment ─────────────────────────────────────────
  {
    id: 'SCF-PPL-001',
    domain: 'PPL',
    title: 'Pre-employment screening',
    description:
      'Background verification checks on all candidates for employment shall be carried out prior to joining and on an ongoing basis where appropriate, in accordance with laws and regulations.',
  },
  {
    id: 'SCF-PPL-002',
    domain: 'PPL',
    title: 'Terms and conditions of employment',
    description:
      "Employment contracts and agreements shall state the personnel's and the organization's responsibilities for information security.",
  },
  {
    id: 'SCF-PPL-003',
    domain: 'PPL',
    title: 'Security awareness, education and training',
    description:
      'Personnel and relevant interested parties shall receive appropriate security awareness education and training, and regular updates, relevant to their job function.',
  },
  {
    id: 'SCF-PPL-004',
    domain: 'PPL',
    title: 'Disciplinary process',
    description:
      'A disciplinary process shall be formalized, communicated and applied to personnel who have committed an information security policy violation.',
  },
  {
    id: 'SCF-PPL-005',
    domain: 'PPL',
    title: 'Post-employment responsibilities',
    description:
      'Information security responsibilities and duties that remain valid after termination or change of employment shall be defined, enforced and communicated to the relevant personnel.',
  },
  {
    id: 'SCF-PPL-006',
    domain: 'PPL',
    title: 'Confidentiality and non-disclosure agreements',
    description:
      "Confidentiality and non-disclosure agreements reflecting the organization's requirements for protection of information shall be identified, documented, regularly reviewed and signed by relevant parties.",
  },
  {
    id: 'SCF-PPL-007',
    domain: 'PPL',
    title: 'Remote working security',
    description:
      "Security measures shall be implemented for personnel working remotely to protect information accessed, processed or stored outside the organization's premises.",
  },
  {
    id: 'SCF-PPL-008',
    domain: 'PPL',
    title: 'Security event reporting',
    description:
      'Personnel shall be provided with a mechanism to report observed or suspected information security events through appropriate channels in a timely manner.',
  },

  // ── PHY — Physical Security ───────────────────────────────────────────
  {
    id: 'SCF-PHY-001',
    domain: 'PHY',
    title: 'Physical security perimeters',
    description:
      'Security perimeters shall be defined and used to protect areas containing information assets, with the strength of perimeter proportionate to the value of the assets inside.',
  },
  {
    id: 'SCF-PHY-002',
    domain: 'PHY',
    title: 'Physical entry controls',
    description:
      'Secure areas shall be protected by appropriate entry controls to ensure only authorized personnel are allowed access.',
  },
  {
    id: 'SCF-PHY-003',
    domain: 'PHY',
    title: 'Securing offices and facilities',
    description:
      'Physical security for offices, rooms and facilities shall be designed and implemented, with measures appropriate to the security requirements.',
  },
  {
    id: 'SCF-PHY-004',
    domain: 'PHY',
    title: 'Physical security monitoring',
    description:
      'Premises shall be continuously or periodically monitored for unauthorized physical access, and monitoring records retained and reviewed.',
  },
  {
    id: 'SCF-PHY-005',
    domain: 'PHY',
    title: 'Environmental threat protection',
    description:
      'Protection against physical and environmental threats such as natural disasters, fire, flood and intentional attacks shall be designed and implemented.',
  },
  {
    id: 'SCF-PHY-006',
    domain: 'PHY',
    title: 'Working in secure areas',
    description:
      'Security measures and guidelines for working in secure areas shall be designed, implemented and communicated to authorized personnel.',
  },
  {
    id: 'SCF-PHY-007',
    domain: 'PHY',
    title: 'Clear desk and clear screen',
    description:
      'Clear desk rules for papers and removable storage media and clear screen rules for information processing facilities shall be defined and enforced.',
  },
  {
    id: 'SCF-PHY-008',
    domain: 'PHY',
    title: 'Equipment siting and protection',
    description:
      'Equipment shall be sited and protected to reduce the risks of physical and environmental threats and unauthorized access.',
  },
  {
    id: 'SCF-PHY-009',
    domain: 'PHY',
    title: 'Off-site asset security',
    description:
      'Assets used outside organizational premises shall be protected with appropriate controls taking into account the additional risks of working outside secure premises.',
  },
  {
    id: 'SCF-PHY-010',
    domain: 'PHY',
    title: 'Storage media management',
    description:
      "Storage media shall be managed through their lifecycle of acquisition, use, transportation and disposal in accordance with the organization's classification scheme.",
  },
  {
    id: 'SCF-PHY-011',
    domain: 'PHY',
    title: 'Supporting utilities',
    description:
      'Information processing facilities shall be protected from power failures and other disruptions caused by failures in supporting utilities such as electricity, water and telecoms.',
  },
  {
    id: 'SCF-PHY-012',
    domain: 'PHY',
    title: 'Cabling security',
    description:
      'Cables carrying power, data or supporting information services shall be protected from interception, interference or damage.',
  },
  {
    id: 'SCF-PHY-013',
    domain: 'PHY',
    title: 'Equipment maintenance',
    description:
      'Equipment shall be maintained correctly to ensure availability, integrity and confidentiality of information, with maintenance records kept.',
  },
  {
    id: 'SCF-PHY-014',
    domain: 'PHY',
    title: 'Secure equipment disposal',
    description:
      'Equipment containing storage media shall be verified to ensure that sensitive data has been removed or securely overwritten prior to disposal or re-use.',
  },

  {
    id: 'SCF-PHY-015',
    domain: 'PHY',
    title: 'Payment terminal (POI) tamper protection',
    description:
      'Point-of-interaction (POI) devices that capture payment card data through direct physical interaction shall be protected against tampering and unauthorized substitution. An up-to-date list of such devices shall be maintained, devices shall be periodically inspected for tampering or substitution, and personnel shall be trained to detect and report suspicious behaviour around terminals.',
  },

  // ── OPS — Operations & Technology ─────────────────────────────────────
  {
    id: 'SCF-OPS-001',
    domain: 'OPS',
    title: 'User endpoint security',
    description:
      'Information stored on, processed by or accessible via user endpoint devices shall be protected through appropriate technical and procedural controls.',
  },
  {
    id: 'SCF-OPS-002',
    domain: 'OPS',
    title: 'Capacity management',
    description:
      'Resource usage shall be monitored and projections made to ensure required system performance to meet business needs, with capacity adjusted as necessary.',
  },
  {
    id: 'SCF-OPS-003',
    domain: 'OPS',
    title: 'Malware protection',
    description:
      'Technical and procedural controls for protection against malware shall be implemented and combined with appropriate user awareness training.',
  },
  {
    id: 'SCF-OPS-004',
    domain: 'OPS',
    title: 'Vulnerability management',
    description:
      "Information about technical vulnerabilities shall be obtained in a timely fashion, the organization's exposure evaluated, and appropriate measures taken to address associated risk.",
  },
  {
    id: 'SCF-OPS-005',
    domain: 'OPS',
    title: 'Configuration management',
    description:
      'Configurations of hardware, software and networks shall be established, documented, implemented, monitored and reviewed to ensure security is maintained throughout their lifecycle.',
  },
  {
    id: 'SCF-OPS-006',
    domain: 'OPS',
    title: 'Security logging',
    description:
      'Event logs recording user activities, exceptions, faults and security events shall be produced, protected, stored and regularly reviewed.',
  },
  {
    id: 'SCF-OPS-007',
    domain: 'OPS',
    title: 'Security monitoring',
    description:
      'Networks, systems and applications shall be continuously or periodically monitored for anomalous behaviour, and appropriate action taken when events are detected.',
  },
  {
    id: 'SCF-OPS-008',
    domain: 'OPS',
    title: 'Clock synchronisation',
    description:
      'The clocks of all relevant information processing systems shall be synchronised to approved time sources to ensure accuracy of log records and event correlation.',
  },
  {
    id: 'SCF-OPS-009',
    domain: 'OPS',
    title: 'Privileged utility programs',
    description:
      'The use of utility programs capable of overriding system and application controls shall be restricted and tightly controlled.',
  },
  {
    id: 'SCF-OPS-010',
    domain: 'OPS',
    title: 'Software installation controls',
    description:
      'Procedures and measures shall be implemented to control the installation of software on operational systems, including authorisation requirements.',
  },
  {
    id: 'SCF-OPS-011',
    domain: 'OPS',
    title: 'Redundancy of processing facilities',
    description:
      'Information processing facilities shall incorporate adequate redundancy to satisfy availability requirements defined by business continuity objectives.',
  },
  {
    id: 'SCF-OPS-012',
    domain: 'OPS',
    title: 'Cardholder data environment scoping',
    description:
      'The cardholder data environment (CDE) shall be formally defined and documented, identifying all system components that store, process, or transmit cardholder data or that could impact the security of the CDE. Scope shall be reviewed and confirmed at least annually and upon significant changes.',
  },

  {
    id: 'SCF-OPS-013',
    domain: 'OPS',
    title: 'Email security and anti-phishing',
    description:
      "Electronic mail services shall be protected against phishing, spoofing and malicious content. Sender-authentication mechanisms (SPF, DKIM and a DMARC enforcement policy) shall be configured for the organization's domains; inbound email shall be filtered for malicious attachments and links, with unnecessary or high-risk file types blocked; and email server anti-malware protections shall be deployed and maintained. Only fully supported email clients and authorised extensions shall be permitted.",
  },

  // ── NET — Network Security ────────────────────────────────────────────
  {
    id: 'SCF-NET-001',
    domain: 'NET',
    title: 'Network security management',
    description:
      'Networks and network devices shall be managed, controlled and documented to protect information in systems and applications, and to maintain security across all environments.',
  },
  {
    id: 'SCF-NET-002',
    domain: 'NET',
    title: 'Security of network services',
    description:
      'Security mechanisms, service levels and management requirements for all network services shall be identified, implemented and monitored.',
  },
  {
    id: 'SCF-NET-003',
    domain: 'NET',
    title: 'Network segregation',
    description:
      'Groups of information services, users and information systems shall be segregated in networks to reduce the risk of unauthorized access and limit the impact of a compromise.',
  },
  {
    id: 'SCF-NET-004',
    domain: 'NET',
    title: 'Web filtering',
    description:
      'Access to external websites shall be managed to reduce the risk of malicious content, and web filtering controls shall be implemented and maintained.',
  },
  {
    id: 'SCF-NET-005',
    domain: 'NET',
    title: 'Cryptography',
    description:
      'A policy on the use of cryptographic controls shall be developed and implemented to protect the confidentiality, authenticity and integrity of information.',
  },

  // ── DEV — Secure Development ──────────────────────────────────────────
  {
    id: 'SCF-DEV-001',
    domain: 'DEV',
    title: 'Secure development lifecycle',
    description:
      'Rules for the secure development of software and systems shall be established and applied throughout the development lifecycle, covering all phases from requirements to deployment.',
  },
  {
    id: 'SCF-DEV-002',
    domain: 'DEV',
    title: 'Application security requirements',
    description:
      'Information security related requirements shall be included when developing or acquiring applications, covering authentication, authorization, input validation and data protection.',
  },
  {
    id: 'SCF-DEV-003',
    domain: 'DEV',
    title: 'Secure architecture and engineering principles',
    description:
      'Principles for engineering secure systems shall be established, documented, maintained and applied to any information system implementation.',
  },
  {
    id: 'SCF-DEV-004',
    domain: 'DEV',
    title: 'Secure coding',
    description:
      'Secure coding principles and practices shall be applied to software development to prevent common vulnerabilities and reduce the attack surface of developed software.',
  },
  {
    id: 'SCF-DEV-005',
    domain: 'DEV',
    title: 'Security testing',
    description:
      'Security testing and evaluation shall be carried out during development and acceptance to validate that security requirements have been met and vulnerabilities addressed.',
  },
  {
    id: 'SCF-DEV-006',
    domain: 'DEV',
    title: 'Outsourced development',
    description:
      'The organization shall supervise and monitor outsourced software development activities and apply security requirements to suppliers involved in development.',
  },
  {
    id: 'SCF-DEV-007',
    domain: 'DEV',
    title: 'Environment separation',
    description:
      'Development, testing and production environments shall be separated and independently controlled to reduce the risks of unauthorized access or changes to the production environment.',
  },
  {
    id: 'SCF-DEV-008',
    domain: 'DEV',
    title: 'Change management',
    description:
      'Changes to information processing facilities and systems shall be controlled through formal change management procedures covering assessment, authorisation, testing and documentation.',
  },
  {
    id: 'SCF-DEV-009',
    domain: 'DEV',
    title: 'Test information protection',
    description:
      'Test information shall be appropriately selected, protected and controlled to prevent unauthorized exposure of production data in test environments.',
  },
  {
    id: 'SCF-DEV-010',
    domain: 'DEV',
    title: 'Audit testing protection',
    description:
      'Requirements and activities involving audit and verification of operational systems shall be carefully planned and agreed to minimise disruptions to business processes.',
  },
  {
    id: 'SCF-DEV-011',
    domain: 'DEV',
    title: 'Payment application security',
    description:
      'Web-facing applications shall be protected against known attacks by applying secure coding practices, conducting annual application security assessments or deploying a web application firewall. Custom application code shall be reviewed for common vulnerabilities prior to deployment.',
  },

  // ── INC — Incident Management ─────────────────────────────────────────
  {
    id: 'SCF-INC-001',
    domain: 'INC',
    title: 'Incident management planning',
    description:
      'The organization shall plan and prepare for managing information security incidents by defining processes, roles, responsibilities and communication channels in advance.',
  },
  {
    id: 'SCF-INC-002',
    domain: 'INC',
    title: 'Incident assessment and classification',
    description:
      'Information security events shall be assessed, classified and prioritised to determine appropriate response actions and distinguish incidents from false positives.',
  },
  {
    id: 'SCF-INC-003',
    domain: 'INC',
    title: 'Incident response and mitigation',
    description:
      'Information security incidents shall be responded to in accordance with documented procedures, including containment, eradication, recovery and communication steps.',
  },
  {
    id: 'SCF-INC-004',
    domain: 'INC',
    title: 'Learning from incidents',
    description:
      'Knowledge gained from analysing and resolving information security incidents shall be used to strengthen controls and reduce the likelihood and impact of future incidents.',
  },
  {
    id: 'SCF-INC-005',
    domain: 'INC',
    title: 'Evidence collection and preservation',
    description:
      'Procedures for the identification, collection, acquisition and preservation of information that can serve as evidence shall be established and followed.',
  },
  {
    id: 'SCF-INC-006',
    domain: 'INC',
    title: 'Regulatory incident notification',
    description:
      'Significant incidents shall be notified to the competent authority, supervisory authority, or CSIRT within the timelines mandated by the applicable regulation, using the reporting stages that regulation requires (for example an early warning, an incident notification, intermediate updates, and a final report). Where personal data is affected and the breach is likely to result in high risk to individuals, affected data subjects shall be informed as required by the applicable regulation. Notification records shall be maintained.',
  },

  {
    id: 'SCF-INC-007',
    domain: 'INC',
    title: 'Incident communication to affected parties',
    description:
      "Where an incident affects the organization's clients, users or other individuals, those affected parties shall be informed of the incident, its likely impact and the measures they can take to mitigate harm, within the timelines required by applicable law or contract. This is distinct from notification to competent authorities and CSIRTs: it targets the parties who rely on the affected service (for example DORA Art. 19(3) client notification, and communication to data subjects analogous to GDPR Art. 34). Records of what was communicated, to whom and when shall be retained.",
  },

  // ── BCM — Business Continuity ─────────────────────────────────────────
  {
    id: 'SCF-BCM-001',
    domain: 'BCM',
    title: 'Information security during disruption',
    description:
      'The organization shall plan how to maintain information security at an appropriate level during disruption, and verify continuity controls at regular intervals.',
  },
  {
    id: 'SCF-BCM-002',
    domain: 'BCM',
    title: 'ICT continuity planning',
    description:
      'ICT readiness shall be planned, implemented, maintained and tested based on business continuity objectives and ICT continuity requirements, including recovery time and point objectives.',
  },
  {
    id: 'SCF-BCM-003',
    domain: 'BCM',
    title: 'Information backup',
    description:
      'Backup copies of information, software and systems shall be maintained and regularly tested in accordance with an agreed backup policy.',
  },

  {
    id: 'SCF-BCM-004',
    domain: 'BCM',
    title: 'Business impact analysis',
    description:
      "A business impact analysis shall be performed to identify the organization's activities, the resources they depend on, and the impact over time of their disruption. The analysis shall establish prioritized recovery objectives — including maximum tolerable periods of disruption and recovery time and point objectives — that drive the selection of continuity strategies and solutions. The business impact analysis shall be reviewed at planned intervals and after significant change.",
  },
  {
    id: 'SCF-BCM-005',
    domain: 'BCM',
    title: 'Business continuity exercising and testing',
    description:
      "Business continuity plans, procedures and solutions shall be exercised and tested at planned intervals and after significant change, using scenarios consistent with the organization's continuity objectives. Exercises shall validate that recovery objectives can be met, and their results shall be documented, reviewed and used to correct and improve continuity arrangements.",
  },

  // ── VDR — Vendor & Supply Chain ───────────────────────────────────────
  {
    id: 'SCF-VDR-001',
    domain: 'VDR',
    title: 'Supplier relationship management',
    description:
      'Processes and procedures shall be defined and implemented to manage information security risks associated with the use of supplier products or services throughout the supplier relationship lifecycle.',
  },
  {
    id: 'SCF-VDR-002',
    domain: 'VDR',
    title: 'Supplier agreements',
    description:
      'Relevant information security requirements shall be established and agreed with each supplier based on the type of supplier relationship and the data or systems involved.',
  },
  {
    id: 'SCF-VDR-003',
    domain: 'VDR',
    title: 'ICT supply chain security',
    description:
      'Processes and procedures shall be defined and implemented to manage information security risks associated with ICT products and services across the supply chain.',
  },
  {
    id: 'SCF-VDR-004',
    domain: 'VDR',
    title: 'Supplier monitoring and review',
    description:
      'Supplier information security practices and service delivery shall be regularly monitored, reviewed, evaluated and managed, including changes that may affect security.',
  },
  {
    id: 'SCF-VDR-005',
    domain: 'VDR',
    title: 'Cloud service security',
    description:
      "Processes for acquisition, use, management and exit from cloud services shall be established in accordance with the organization's information security requirements.",
  },

  // ── DAT — Data Protection ─────────────────────────────────────────────
  {
    id: 'SCF-DAT-001',
    domain: 'DAT',
    title: 'Records and data protection',
    description:
      'Records and information shall be protected from loss, destruction, falsification, unauthorized access and unauthorized release in accordance with legal and business requirements. Technical and organisational measures shall ensure appropriate security of personal data, including protection against unauthorised or unlawful processing and against accidental loss, in accordance with Article 32 (security of processing).',
  },
  {
    id: 'SCF-DAT-002',
    domain: 'DAT',
    title: 'Privacy and PII protection',
    description:
      'The organization shall identify and meet requirements for the preservation of privacy and protection of personally identifiable information in accordance with applicable laws and regulations.',
  },
  {
    id: 'SCF-DAT-003',
    domain: 'DAT',
    title: 'Information deletion',
    description:
      'Information stored in information systems, devices or in any other storage media shall be deleted when no longer required, using secure deletion methods appropriate to the sensitivity of the data. Personal data shall not be kept in a form which permits identification of data subjects for longer than is necessary for the purposes for which it is processed, in accordance with Article 5(1)(e) (storage limitation).',
  },
  {
    id: 'SCF-DAT-004',
    domain: 'DAT',
    title: 'Data masking',
    description:
      "Data masking shall be used in accordance with the organization's access control policy and other related policies, and business requirements, considering applicable legislation.",
  },
  {
    id: 'SCF-DAT-005',
    domain: 'DAT',
    title: 'Data leakage prevention',
    description:
      'Measures to prevent unauthorized disclosure of sensitive information shall be applied to systems, networks and any other devices that process, store or transmit sensitive information.',
  },

  {
    id: 'SCF-DAT-006',
    domain: 'DAT',
    title: 'Data portability and transfer',
    description:
      'Where required by law or contract, the organization shall be able to provide data to the party entitled to it in a structured, commonly used and machine-readable format, and to transmit it to another recipient where technically feasible. This covers the data-portability half of "erase or transfer" requirements — the technical capability to export or hand over data on request — as distinct from secure deletion, and supports data subject portability rights and product data-transfer obligations.',
  },

  // ── PRV — Privacy Rights ──────────────────────────────────────────────
  {
    id: 'SCF-PRV-001',
    domain: 'PRV',
    title: 'Lawful basis for processing',
    description:
      'All personal data processing activities shall have a documented lawful basis under Article 6 (or Article 9 for special categories). The lawful basis shall be identified before processing begins and documented in the records of processing activities.',
  },
  {
    id: 'SCF-PRV-002',
    domain: 'PRV',
    title: 'Records of processing activities (ROPA)',
    description:
      'The organisation shall maintain records of all processing activities under its responsibility, including: purpose of processing, categories of data subjects and personal data, recipients, transfers to third countries, retention periods, and security measures. Records shall be kept in writing and made available to the supervisory authority on request.',
  },
  {
    id: 'SCF-PRV-003',
    domain: 'PRV',
    title: 'Data subject rights management',
    description:
      'Procedures shall exist to receive, validate and respond to data subject requests for access, rectification, erasure, restriction, portability, and objection within the statutory one-month deadline. Requests shall be logged with receipt date, action taken, and response date.',
  },
  {
    id: 'SCF-PRV-004',
    domain: 'PRV',
    title: 'Data Protection Impact Assessment (DPIA)',
    description:
      'A Data Protection Impact Assessment shall be conducted prior to any processing likely to result in high risk to individuals, including large-scale processing of special category data, systematic monitoring, or use of new technologies. The DPIA shall describe processing, assess necessity, identify risks, and document mitigations.',
  },
  {
    id: 'SCF-PRV-005',
    domain: 'PRV',
    title: 'Data Protection Officer (DPO)',
    description:
      'Where required under Article 37, a Data Protection Officer shall be designated, their contact details published, and notified to the supervisory authority. The DPO shall have expert knowledge of data protection law and shall not receive instructions regarding the exercise of their tasks.',
  },
  {
    id: 'SCF-PRV-006',
    domain: 'PRV',
    title: 'International data transfers',
    description:
      'Personal data shall only be transferred to third countries or international organisations where an adequate level of protection is ensured, including through adequacy decisions, standard contractual clauses, binding corporate rules, or other approved transfer mechanisms.',
  },
  {
    id: 'SCF-PRV-007',
    domain: 'PRV',
    title: 'Privacy by design and default',
    description:
      'Data protection principles shall be implemented by design and by default. Privacy controls shall be integrated into system and process design from inception. By default, only personal data necessary for each specific purpose shall be processed.',
  },
  {
    id: 'SCF-PRV-008',
    domain: 'PRV',
    title: 'Consent management',
    description:
      'Where processing is based on consent, the organisation shall be able to demonstrate that the data subject has consented. Consent shall be freely given, specific, informed, and unambiguous. Records of consent shall be maintained. Withdrawal of consent shall be as easy as giving it.',
  },

  {
    id: 'SCF-PRV-009',
    domain: 'PRV',
    title: 'Transparency and privacy notices',
    description:
      "Data subjects shall be provided, in a concise, transparent, intelligible and easily accessible form, with the information required at the time personal data is collected — whether obtained directly from the data subject or from another source — including the identity of the controller, the purposes and lawful basis of processing, recipients, retention periods, the data subject's rights and, where applicable, the source of the data (GDPR Articles 12–14). Privacy notices shall be maintained current and made available wherever personal data is collected.",
  },

  // ── AIG — AI Governance ───────────────────────────────────────────────
  {
    id: 'SCF-AIG-001',
    domain: 'AIG',
    title: 'AI policy',
    description:
      'A topic-specific policy for artificial intelligence shall be defined, approved by management, communicated and reviewed at planned intervals. The AI policy shall be aligned with other organizational policies, state the principles for responsible AI (fairness, transparency, accountability, human oversight, safety), and set the objectives for AI management.',
  },
  {
    id: 'SCF-AIG-002',
    domain: 'AIG',
    title: 'AI roles, responsibilities and concern reporting',
    description:
      'Roles and responsibilities for the AI management system shall be defined, allocated and communicated, including responsibility for AI policy, AI risk, AI system design, deployment and operation. A mechanism shall be provided for personnel and external stakeholders to raise concerns about AI systems, with protections against retaliation.',
  },
  {
    id: 'SCF-AIG-003',
    domain: 'AIG',
    title: 'AI resource inventory',
    description:
      'An inventory of resources used to develop, deploy and operate AI systems shall be maintained, covering data resources, tooling, system and computing resources, and the human resources (competencies, roles) needed. The inventory shall be reviewed as AI systems change across the lifecycle.',
  },
  {
    id: 'SCF-AIG-004',
    domain: 'AIG',
    title: 'AI system impact assessment',
    description:
      'A process for assessing the impact of AI systems on individuals, groups and society shall be defined, applied before deployment and re-applied after material change. Assessments shall address foreseeable harms, fundamental rights, fairness, safety, autonomy, and environmental and societal effects. Results shall be documented and drive risk treatment decisions.',
  },
  {
    id: 'SCF-AIG-005',
    domain: 'AIG',
    title: 'Responsible AI design and development',
    description:
      'Objectives and processes for responsible AI development shall be established, covering requirements specification, design, documentation, threat modelling and the embedding of fairness, robustness, explainability, safety and security into the AI system by design. Deviations from the objectives shall be justified and recorded.',
  },
  {
    id: 'SCF-AIG-006',
    domain: 'AIG',
    title: 'AI verification, validation and deployment',
    description:
      'AI systems shall be verified and validated before deployment and before significant changes, including accuracy, robustness, bias, security and performance testing under realistic conditions. Deployment shall follow a controlled process including go/no-go criteria, rollback plans and approval records.',
  },
  {
    id: 'SCF-AIG-007',
    domain: 'AIG',
    title: 'AI system operation, monitoring and event logging',
    description:
      'AI systems in production shall be continuously monitored for performance, drift, bias, safety, misuse and security events. Automatically generated event logs shall be retained and protected to a level consistent with the risk of the AI system and legal obligations, and reviewed to detect anomalies and to support post-market monitoring, investigations and audits.',
  },
  {
    id: 'SCF-AIG-008',
    domain: 'AIG',
    title: 'AI technical documentation and user information',
    description:
      'Technical documentation for each AI system shall be drawn up and maintained, covering system purpose, intended use, performance metrics, data and model characteristics, known limitations, and operational constraints. Information provided to users and affected persons shall be clear, accessible and sufficient to allow appropriate use, oversight and challenge of outcomes.',
  },
  {
    id: 'SCF-AIG-009',
    domain: 'AIG',
    title: 'Data quality, provenance and preparation for AI',
    description:
      'Data used for training, validation, testing and operation of AI systems shall be governed for quality, relevance, representativeness and freedom from bias, with documented provenance. Data preparation activities (cleaning, labelling, aggregation, synthetic data generation) shall be documented and applied consistently with the intended purpose of the AI system.',
  },
  {
    id: 'SCF-AIG-010',
    domain: 'AIG',
    title: 'External reporting and AI incident communication',
    description:
      'Channels and procedures shall be established for external reporting on AI systems, including communication of serious incidents, malfunctions and material changes to authorities, affected persons and other interested parties, within the timelines required by law or contract. Records of communications shall be retained.',
  },
  {
    id: 'SCF-AIG-011',
    domain: 'AIG',
    title: 'Responsible use and human oversight of AI systems',
    description:
      'Processes for the responsible use of AI systems shall define their intended use, prohibited uses, the human oversight measures in place (including the ability to intervene, override or stop the system), and the competence required of operators. Deviations from the intended use shall trigger reassessment.',
  },
  {
    id: 'SCF-AIG-012',
    domain: 'AIG',
    title: 'Third-party AI supplier and customer management',
    description:
      "Responsibilities shall be allocated across the AI value chain — including suppliers of AI components, models, data and services, and customers / deployers of the organization's AI systems. Contracts and agreements shall document information, support and cooperation obligations sufficient to allow each party to meet its own AI governance duties.",
  },
  {
    id: 'SCF-AIG-013',
    domain: 'AIG',
    title: 'Prohibited AI practices screening',
    description:
      'Before design, acquisition, deployment or significant change of an AI system, it shall be screened against the list of prohibited AI practices (EU AI Act Art. 5 — e.g. manipulative techniques, exploitation of vulnerabilities, social scoring, unfocused scraping of facial images, emotion recognition in workplaces and schools, biometric categorisation of sensitive characteristics, real-time remote biometric identification in public spaces). Prohibited practices shall not be placed on the market, put into service or used. Screening outcomes shall be documented.',
  },
  {
    id: 'SCF-AIG-014',
    domain: 'AIG',
    title: 'Fundamental rights impact assessment',
    description:
      'Deployers of high-risk AI systems that are bodies governed by public law, private operators providing public services, or that use AI systems for creditworthiness, insurance pricing or law enforcement shall conduct a fundamental rights impact assessment prior to first use and after material change (EU AI Act Art. 27). The FRIA shall describe the deployer process, duration and frequency of use, categories of affected natural persons, specific harms to fundamental rights, human-oversight measures and the measures to take if those risks materialise. Results shall be notified to the market-surveillance authority.',
  },
  {
    id: 'SCF-AIG-015',
    domain: 'AIG',
    title: 'Conformity assessment, EU declaration and CE marking for high-risk AI',
    description:
      'Providers of high-risk AI systems shall subject each such system to the applicable conformity-assessment procedure (EU AI Act Art. 43), draw up an EU declaration of conformity (Art. 47), affix the CE marking (Art. 48) and register the system in the EU database for high-risk AI (Art. 49, 71) before placing it on the market or putting it into service. Records supporting conformity shall be retained for the period required by the Regulation.',
  },
  {
    id: 'SCF-AIG-016',
    domain: 'AIG',
    title: 'Post-market monitoring of high-risk AI and serious-incident reporting',
    description:
      'Providers of high-risk AI systems shall establish and document a post-market monitoring system proportionate to the nature of the AI technologies and the risks of the system (EU AI Act Art. 72). Serious incidents shall be reported to the market-surveillance authority of the Member State where the incident occurred within the statutory timelines: any serious incident within 15 days of awareness; widespread infringement or serious and irreversible disruption of critical infrastructure within 2 days; fatality within 10 days (Art. 73). Investigation outcomes and corrective actions shall be documented.',
  },
  {
    id: 'SCF-AIG-017',
    domain: 'AIG',
    title: 'General-purpose AI model provider obligations',
    description:
      'Providers of general-purpose AI (GPAI) models shall draw up and keep up to date technical documentation of the model, provide information to downstream providers that integrate the model, put in place a policy to comply with EU copyright law, and publish a sufficiently detailed summary of the content used for training (EU AI Act Art. 53). Providers of GPAI models with systemic risk shall additionally perform model evaluations including adversarial testing, assess and mitigate systemic risks, ensure adequate cybersecurity protection and report serious incidents to the AI Office (Art. 55). Authorised representatives shall be appointed where the provider is established outside the Union (Art. 54).',
  },
  {
    id: 'SCF-AIG-018',
    domain: 'AIG',
    title: 'AI risk management system',
    description:
      'A continuous, iterative risk management system shall be established, implemented, documented and maintained across the whole lifecycle of each high-risk AI system (EU AI Act Art. 9). It shall identify and analyse the known and reasonably foreseeable risks the system can pose to health, safety and fundamental rights, evaluate risks arising from intended use and reasonably foreseeable misuse, adopt targeted risk-management measures, and be updated with information from post-market monitoring. This whole-lifecycle risk process is distinct from a point-in-time impact assessment: it runs and is re-run throughout development and operation rather than being completed once before deployment.',
  },
];

module.exports = {
  SCF_DOMAINS,
  SCF_CONTROLS,
};
